// spousalCalculator.js

// ----------------- Utility: FRA from birth year -----------------
export function getFRAFromBirthYear(birthYear) {
  // Social Security FRA schedule (simplified):
  // 1943-1954 => 66y0m
  // 1955 => 66y2m
  // 1956 => 66y4m
  // 1957 => 66y6m
  // 1958 => 66y8m
  // 1959 => 66y10m
  // 1960+ => 67y0m
  const y = Number(birthYear);
  if (isNaN(y)) return { fraYears: 66, fraMonths: 0 };
  if (y >= 1960) return { fraYears: 67, fraMonths: 0 };
  if (y >= 1959) return { fraYears: 66, fraMonths: 10 };
  if (y >= 1958) return { fraYears: 66, fraMonths: 8 };
  if (y >= 1957) return { fraYears: 66, fraMonths: 6 };
  if (y >= 1956) return { fraYears: 66, fraMonths: 4 };
  if (y >= 1955) return { fraYears: 66, fraMonths: 2 };
  return { fraYears: 66, fraMonths: 0 };
}

// ----------------- Helper: convert years+months -> total months -----------------
function totalMonthsFromYearsMonths(years, months = 0) {
  return Number(years) * 12 + Number(months || 0);
}
// export function ageAtBreakeven(claim_age, monthsToBreakEven) {
//   const totalMonths = claim_age * 12 + monthsToBreakEven;
//   const years = Math.floor(totalMonths / 12);
//   const months = Math.round(totalMonths % 12);
//   const formatted = `${years} years and ${months} months`;
//   return { years, months, decimal: totalMonths / 12, formatted };
// }
export function ageAtBreakeven(claim_age, monthsToBreakEven) {
  const totalMonths = claim_age * 12 + monthsToBreakEven;
  const years = Math.floor(totalMonths / 12);
  const months = Math.floor(totalMonths % 12);
  const formatted = `${years} years and ${months} months`;
  return { years, months, decimal: totalMonths / 12, formatted };
}
// ----------------- % of spousal FRA (reduction for claiming early) -----------------
// Social Security spousal early reduction logic (matches your sheet):
// - For the first 36 months early: monthly reduction = (25/36) of 1%  (i.e. (25/36)/100 per month)
// - After 36 months early: monthly reduction = (5/12) of 1%           (i.e. (5/12)/100 per month)
export function percentOfSpousalFRA(
  claimYears,
  claimMonths,
  FRA_years,
  FRA_months
) {
  const fraTotal = totalMonthsFromYearsMonths(FRA_years, FRA_months);
  const claimTotal = totalMonthsFromYearsMonths(claimYears, claimMonths);
  const monthsEarly = Math.max(0, fraTotal - claimTotal); // positive when claimed before FRA

  if (monthsEarly <= 0) return 1; // at or after FRA → 100%

  const first36 = Math.min(monthsEarly, 36);
  const remaining = Math.max(0, monthsEarly - 36);

  const reduction = first36 * (5 / 900) + remaining * (5 / 1200); // exact Excel logic
  const pct = 1 - reduction;

  return Math.max(0, pct);
}

// ----------------- monthly spousal benefit (raw number) -----------------
export function monthlySpousalBenefit(fraSpousalBenefit, pctOfFRA) {
  return Number(fraSpousalBenefit || 0) * Number(pctOfFRA || 0);
}

// ----------------- total collected before FRA (pre-FRA benefit) -----------------
// export function preFRABenefit(monthlyBenefit, monthsEarly) {
//   return monthlyBenefit * Math.max(0, Math.round(monthsEarly || 0));
// }

export function preFRABenefit(monthlyBenefit, monthsEarly) {
  return monthlyBenefit * Math.max(0, monthsEarly || 0); // ✅ remove rounding
}

// ----------------- future value at FRA of pre-FRA payments (with COLA & discount) -----------------
export function futureValueAtFRA(
  monthlyBenefit,
  monthsEarly,
  cola = 0.02,
  discountRate = 0.03
) {
  // monthsEarly = integer months before FRA
  monthsEarly = Math.max(0, Math.round(monthsEarly || 0));
  if (monthsEarly === 0 || monthlyBenefit === 0) return 0;

  let fv = 0;
  // For each payment received before FRA, grow it forward to FRA using COLA,
  // optionally discounting using discountRate (keeps formula flexible)
  // i loops payments from oldest (farthest from FRA) to newest
  for (let i = 0; i < monthsEarly; i++) {
    const monthsUntilFRA = monthsEarly - i; // e.g., if i=0 (oldest) monthsUntilFRA = monthsEarly
    const colaFactor = Math.pow(1 + cola, monthsUntilFRA / 12);
    const discountFactor = Math.pow(1 + discountRate, monthsUntilFRA / 12);
    fv += monthlyBenefit * (colaFactor / discountFactor);
  }
  return fv;
}

// ----------------- Break-even helper -----------------
function safeBreakEven(numerator, denom) {
  if (!isFinite(numerator) || !isFinite(denom) || denom <= 0) return 0;
  return numerator / denom;
}

// ----------------- Main row calculation -----------------
export function calculateSpousalRow(
  claimYears,
  claimMonths,
  FRA_years,
  FRA_months,
  FRA_spousalBenefit,
  cola = 0.02,
  discountRate = 0.03
) {
  // Normalize numeric inputs
  claimYears = Number(claimYears || 0);
  claimMonths = Number(claimMonths || 0);
  FRA_years = Number(FRA_years || 66);
  FRA_months = Number(FRA_months || 0);
  FRA_spousalBenefit = Number(FRA_spousalBenefit || 0);

  const fraTotalMonths = totalMonthsFromYearsMonths(FRA_years, FRA_months);
  const claimTotalMonths = totalMonthsFromYearsMonths(claimYears, claimMonths);

  const monthsEarly = Math.max(0, fraTotalMonths - claimTotalMonths); // positive if claiming before FRA

  // Percent of FRA
  const pctFRA = percentOfSpousalFRA(
    claimYears,
    claimMonths,
    FRA_years,
    FRA_months
  );

  // Monthly benefit (numeric)
  const monthlyBenefit = monthlySpousalBenefit(FRA_spousalBenefit, pctFRA);

  // Pre-FRA benefit (total collected before FRA)
  const preFRA = preFRABenefit(monthlyBenefit, monthsEarly);

  // Benefit at FRA (spousal at FRA is just FRA_spousalBenefit)
  const benefitAtFRA = FRA_spousalBenefit;

  // Break-even WITHOUT TVM/COLA
  const denom = benefitAtFRA - monthlyBenefit;
  const monthsToBE =
    monthsEarly > 0 && denom > 0 ? safeBreakEven(preFRA, denom) : 0;
  const ageAtBEObj = ageAtBreakeven(claimYears, monthsToBE);
  // const ageAtBE = monthsToBE > 0 ? ageAtBEObj.decimal : claimTotalMonths / 12;
  const ageAtBE = monthsToBE > 0 ? claimYears + monthsToBE / 12 : claimYears;

  // const ageAtBE =
  //   monthsToBE > 0
  //     ? (claimTotalMonths + monthsToBE) / 12
  //     : claimTotalMonths / 12;

  // Break-even WITH TVM + COLA
  const fvAtFRA =
    monthsEarly > 0
      ? futureValueAtFRA(monthlyBenefit, monthsEarly, cola, discountRate)
      : 0;
  const monthsToBE_COLA =
    monthsEarly > 0 && denom > 0 ? safeBreakEven(fvAtFRA, denom) : 0;
  const ageAtBE_COLA_Obj = ageAtBreakeven(claimYears, monthsToBE_COLA);
  // const ageAtBE_COLA =
  //   monthsToBE_COLA > 0 ? ageAtBE_COLA_Obj.decimal : claimTotalMonths / 12;
  const ageAtBE_COLA =
    monthsToBE_COLA > 0 ? claimYears + monthsToBE_COLA / 12 : claimYears;

  // const ageAtBE_COLA =
  //   monthsToBE_COLA > 0
  //     ? (claimTotalMonths + monthsToBE_COLA) / 12
  //     : claimTotalMonths / 12;

  // Formatting for display (strings) + keep numeric fields if needed
  return {
    key: `${claimYears}-${claimMonths}`,
    "Early start age": `${claimYears}${claimMonths ? `/${claimMonths}m` : ""}`,
    "months before FRA": monthsEarly,
    "% of spouse FRA": (pctFRA * 100).toFixed(2),
    "Monthly Benefit": `${monthlyBenefit.toFixed(2)}`,
    monthlyBenefitValue: monthlyBenefit, // numeric if you want to compute later
    "Pre-FRA benefit": `${preFRA.toFixed(2)}`,
    preFRABenefitValue: preFRA,
    "Months to Break-even": monthsToBE > 0 ? Number(monthsToBE.toFixed(2)) : 0,
    "Age at Break-even":
      monthsToBE > 0
        ? Number(ageAtBE.toFixed(2))
        : Number((claimTotalMonths / 12).toFixed(2)),
    "Age at Break-even-formated": ageAtBEObj.formatted,
    "Future value at FRA of pre-FRA benefit": `$${fvAtFRA.toFixed(2)}`,
    futureValueAtFRAValue: fvAtFRA,
    "Months to Break-even (TVM+COLA)":
      monthsToBE_COLA > 0 ? Number(monthsToBE_COLA.toFixed(2)) : 0,
    "Age at Break-even (TVM+COLA)":
      monthsToBE_COLA > 0
        ? Number(ageAtBE_COLA.toFixed(2))
        : Number((claimTotalMonths / 12).toFixed(2)),
    "Age at Break-even-COLA-formated": ageAtBE_COLA_Obj.formatted,
  };
}

// ----------------- Generate a full table: ages from minAge -> maxAge (inclusive) -----------------
export function generateSpousalTable(
  FRA_years,
  FRA_months,
  FRA_spousalBenefit,
  cola = 0.02,
  discountRate = 0.03,
  minAge = 62,
  maxAge /* if not provided, default to FRA_years + 3 */
) {
  if (typeof maxAge === "undefined" || maxAge === null) maxAge = FRA_years + 3;

  const rows = [];
  for (let age = minAge; age <= maxAge; age++) {
    rows.push(
      calculateSpousalRow(
        age,
        0,
        FRA_years,
        FRA_months,
        FRA_spousalBenefit,
        cola,
        discountRate
      )
    );
  }
  // Add FRA row at exact FRA months (e.g., 66y4m) if FRA_months !== 0
  if (FRA_months > 0) {
    rows.push(
      calculateSpousalRow(
        FRA_years,
        FRA_months,
        FRA_years,
        FRA_months,
        FRA_spousalBenefit,
        cola,
        discountRate
      )
    );
  }
  return rows;
}

const FRA_RULES = [
  { year: 1937, fraYears: 65, fraMonths: 0 },
  { year: 1938, fraYears: 65, fraMonths: 2 },
  { year: 1939, fraYears: 65, fraMonths: 4 },
  { year: 1940, fraYears: 65, fraMonths: 6 },
  { year: 1941, fraYears: 65, fraMonths: 8 },
  { year: 1942, fraYears: 65, fraMonths: 10 },
  { year: 1943, fraYears: 66, fraMonths: 0 },
  { year: 1954, fraYears: 66, fraMonths: 0 },
  { year: 1955, fraYears: 66, fraMonths: 2 },
  { year: 1956, fraYears: 66, fraMonths: 4 },
  { year: 1957, fraYears: 66, fraMonths: 6 },
  { year: 1958, fraYears: 66, fraMonths: 8 },
  { year: 1959, fraYears: 66, fraMonths: 10 },
  { year: 1960, fraYears: 67, fraMonths: 0 }, // 1960 and later = 67
];

export const calculateSpouseRemainingRetirementAge = (
  spouseYearOfBirth,
  currentAgeYears,
  currentAgeMonths = 0
) => {
  if (!spouseYearOfBirth || currentAgeYears === undefined) return null;

  let year;

  // 📌 Case 1: Date string or Date object
  if (
    typeof spouseYearOfBirth === "string" &&
    !isNaN(Date.parse(spouseYearOfBirth))
  ) {
    year = new Date(spouseYearOfBirth).getFullYear();
  }
  // 📌 Case 2: Already a year (e.g. 1956)
  else if (typeof spouseYearOfBirth === "number" && spouseYearOfBirth > 1900) {
    year = spouseYearOfBirth;
  }
  // 📌 Case 3: Looks like age (<= 120)
  else if (typeof spouseYearOfBirth === "number" && spouseYearOfBirth <= 120) {
    const currentYear = new Date().getFullYear();
    year = currentYear - spouseYearOfBirth;
  } else {
    return null; // invalid input
  }

  // find FRA for derived year
  let fra = FRA_RULES.find((r) => r.year === year);
  if (!fra) {
    fra =
      year > 1960
        ? { fraYears: 67, fraMonths: 0 }
        : { fraYears: 66, fraMonths: 0 };
  }

  // convert ages to total months
  const fraTotalMonths = fra.fraYears * 12 + fra.fraMonths;
  const currentTotalMonths = currentAgeYears * 12 + currentAgeMonths;

  // how many months left until FRA
  let diff = fraTotalMonths - currentTotalMonths;

  if (diff <= 0) {
    return `0 Years and 0 Months`;
  }

  const remainingYears = Math.floor(diff / 12);
  const remainingMonths = diff % 12;

  return `${remainingYears} Years and ${remainingMonths} Months`;
};
